import React from 'react'
import { MdChevronRight } from 'react-icons/md'

/**
 * Peças do dashboard da home. Só apresentação — nenhuma delas busca dado.
 * As classes vivem em ./styles.ts, dentro do Container da página.
 */

export type Accent = 'blue' | 'green' | 'amber' | 'cyan' | 'slate'
export type Tone = 'gray' | 'green' | 'blue' | 'amber'

/**
 * Cartão: cabeçalho fixo (ícone, título, apoio, recorte) + corpo que ocupa o
 * resto da altura. O corpo é `min-height: 0` para que gráfico e lista possam
 * encolher dentro dele em vez de estourar a borda.
 */
export const DashCard: React.FC<{
  icon: React.ReactNode
  title: string
  accent?: Accent
  subtitle?: React.ReactNode
  /** Etiqueta do recorte (período, mês) à direita do título. */
  pill?: React.ReactNode
  /** Corpo sem respiro lateral — para listas em que a linha inteira é clicável. */
  flush?: boolean
}> = ({ icon, title, accent = 'blue', subtitle, pill, flush, children }) => (
  <div className="dash-card">
    <div className="dash-card__header">
      <div className={`dash-card__icon is-${accent}`}>{icon}</div>
      <div className="dash-card__title">
        <strong title={title}>{title}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
      {pill ? <span className="dash-pill">{pill}</span> : null}
    </div>
    <div className={`dash-card__body${flush ? ' is-flush' : ''}`}>
      {children}
    </div>
  </div>
)

export const DashKpi: React.FC<{
  label: string
  value: React.ReactNode
  accent?: boolean
}> = ({ label, value, accent }) => (
  <div className="dash-kpi">
    <span className="dash-kpi__label">{label}</span>
    <span className={`dash-kpi__value${accent ? ' is-accent' : ''}`}>
      {value}
    </span>
  </div>
)

/**
 * Linha de lista. É um <button> e não uma <tr> de propósito: o alvo de clique
 * passa a ser a linha inteira (antes era só o olhinho de 16px no fim) e o
 * teclado alcança o item.
 */
export const DashRow: React.FC<{
  onClick: () => void
  title: React.ReactNode
  sub?: React.ReactNode
  tag?: { label: string; tone?: Tone }
  value?: React.ReactNode
  valueIsPositive?: boolean
  date?: React.ReactNode
  /** Texto do title/aria quando o conteúdo visível está truncado. */
  label?: string
}> = ({ onClick, title, sub, tag, value, valueIsPositive, date, label }) => (
  <button type="button" className="dash-row" onClick={onClick} title={label}>
    <span className="dash-row__main">
      <span className="dash-row__title">{title}</span>
      {sub ? (
        <span className="dash-row__sub">
          <span>{sub}</span>
        </span>
      ) : null}
    </span>
    {tag || value || date ? (
      <span className="dash-row__side">
        {tag ? (
          <span className={`dash-tag is-${tag.tone || 'gray'}`}>
            {tag.label}
          </span>
        ) : null}
        {value ? (
          <span
            className={`dash-row__value${valueIsPositive ? ' is-green' : ''}`}
          >
            {value}
          </span>
        ) : null}
        {date ? <span className="dash-row__date">{date}</span> : null}
      </span>
    ) : null}
    <MdChevronRight className="dash-row__chevron" />
  </button>
)

/**
 * Carregando / sem resultado / conteúdo. O esqueleto evita o salto de altura
 * entre a primeira pintura e a chegada do dado.
 */
export const DashListState: React.FC<{
  loading: boolean
  isEmpty: boolean
  icon: React.ReactNode
  emptyTitle: string
  emptyText?: string
  skeletonRows?: number
}> = ({
  loading,
  isEmpty,
  icon,
  emptyTitle,
  emptyText,
  skeletonRows = 6,
  children
}) => {
  if (loading) {
    return (
      <>
        {Array.from({ length: skeletonRows }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="dash-skeleton" key={index}>
            <i />
            <i />
          </div>
        ))}
      </>
    )
  }

  if (isEmpty) {
    return (
      <div className="dash-empty">
        {icon}
        <strong>{emptyTitle}</strong>
        {emptyText ? <span>{emptyText}</span> : null}
      </div>
    )
  }

  return <>{children}</>
}

export const DashLegend: React.FC<{
  items: { label: string; color: string; value?: React.ReactNode }[]
}> = ({ items }) => (
  <div className="dash-legend">
    {items.map(item => (
      <span className="dash-legend__item" key={item.label}>
        <i style={{ background: item.color }} />
        {item.label}
        {item.value !== undefined ? <b>{item.value}</b> : null}
      </span>
    ))}
  </div>
)
